import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, FalhaDaApi } from '../../api/cliente';
import {
  Alerta,
  Botao,
  Carregando,
  Cartao,
  Ficha,
  TituloDeSecao,
  Vazio,
} from '../../componentes/base';
import { Campo } from '../../componentes/campos';
import { data, dataHora, documento, moeda, telefone } from '../../util/formato';
import {
  DocumentosDoCliente,
  type DocumentoDoCliente,
} from '../componentes/DocumentosDoCliente';
import { useFasesDoPortal } from '../dados';

interface Marco {
  readonly data: string;
  readonly tipo: string;
  readonly descricao: string;
  readonly criadoEm: string;
}

interface FichaDoPortal {
  readonly devedor: {
    id: string;
    nome: string;
    documento: string | null;
    telefone: string | null;
    telefoneExtra: string | null;
    email: string | null;
    logradouro: string | null;
    cidade: string | null;
    uf: string | null;
    cep: string | null;
    valorOriginal: number;
    vencimentoOriginal: string | null;
    descricaoDivida: string | null;
    pastaDocumentos: string | null;
    fase: string;
    clinica: { id: string; nome: string };
  };
  readonly documentos: DocumentoDoCliente[];
  readonly marcos: Marco[];
}

/**
 * Ficha do cliente no portal.
 *
 * A tela é de LEITURA, com uma exceção: os dados de contato. É a divisão de trabalho
 * do portal inteiro — o dinheiro, o acordo e a fase são conduzidos pelo escritório e
 * aqui só se acompanham; o telefone certo é o que a clínica tem e o escritório não,
 * e telefone errado é a causa número um de caso parado.
 *
 * O histórico interno de negociação não aparece: a clínica vê os MARCOS do caso, não
 * a cozinha do escritório. Quem filtra isso é o servidor, não esta tela.
 */
export function PaginaFichaDoPortal() {
  const { token = '', id = '' } = useParams();
  const fases = useFasesDoPortal();
  const base = `/portal/${token}`;

  const { data: ficha, isLoading } = useQuery({
    queryKey: ['portal', 'devedor', id],
    queryFn: () => api.buscar<FichaDoPortal>(`/portal/devedores/${id}`),
  });

  if (isLoading) return <Carregando />;
  if (!ficha) {
    return (
      <Vazio
        titulo="Este cliente não está na sua carteira."
        saida={
          <Link
            to={`${base}/clientes`}
            className="text-sm text-[color:var(--acento)] underline underline-offset-2"
          >
            Voltar para os clientes
          </Link>
        }
      />
    );
  }

  const { devedor, documentos, marcos } = ficha;
  const fase = fases.get(devedor.fase);

  return (
    <>
      <Link
        to={`${base}/clientes`}
        className="text-xs text-[color:var(--texto-suave)] hover:text-[color:var(--acento)]"
      >
        ← Todos os clientes
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-4 pt-3 pb-6">
        <div>
          <h1 className="text-[1.75rem] leading-tight text-[color:var(--texto)]">
            {devedor.nome}
          </h1>
          <p className="mt-1 font-mono text-xs text-[color:var(--texto-tenue)]">
            {documento(devedor.documento)}
          </p>
          <p className="mt-0.5 text-xs text-[color:var(--texto-suave)]">
            {devedor.clinica.nome}
          </p>
        </div>
        {fase && <Ficha cor={fase.cor}>{fase.rotulo}</Ficha>}
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-6">
          <Cartao>
            <TituloDeSecao auxiliar="conduzido pelo escritório">A dívida</TituloDeSecao>
            <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <Dado rotulo="Valor na origem" valor={moeda(devedor.valorOriginal)} />
              <Dado rotulo="Vencimento" valor={data(devedor.vencimentoOriginal)} />
              <Dado rotulo="Referente a" valor={devedor.descricaoDivida ?? '—'} />
            </dl>
            {/* O contrato mora no Drive da clínica, não aqui. O link é o caminho
                até ele — abrir em aba nova, porque a pessoa volta para a ficha. */}
            {devedor.pastaDocumentos && (
              <p className="mt-4 border-t pt-3 text-sm">
                <a
                  href={devedor.pastaDocumentos}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[color:var(--acento)] underline underline-offset-2"
                >
                  Abrir a pasta deste cliente no Drive →
                </a>
              </p>
            )}

            <p className="mt-4 border-t pt-3 text-xs text-[color:var(--texto-tenue)]">
              Valores, acordos e parcelas são lançados pelo escritório. Se algum número
              aqui estiver errado, avise a equipe em vez de cadastrar o caso de novo.
            </p>
          </Cartao>

          <Cartao semPadding>
            <DocumentosDoCliente devedorId={devedor.id} documentos={documentos} />
          </Cartao>

          <Cartao>
            <TituloDeSecao auxiliar={`${marcos.length} registros`}>
              Andamento do caso
            </TituloDeSecao>
            <LinhaDoTempo marcos={marcos} />
          </Cartao>
        </div>

        <FormularioDeContato devedor={devedor} />
      </div>
    </>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
        {rotulo}
      </dt>
      <dd className="tabular mt-1 text-[color:var(--texto)]">{valor}</dd>
    </div>
  );
}

/**
 * Os dados de contato — a única coisa que a clínica edita nesta tela.
 *
 * O formulário nasce preenchido com o que está no sistema e é semeado por efeito:
 * a ficha chega depois da primeira renderização, e um estado inicial vazio deixaria
 * os campos em branco sobre um cadastro que existe — convidando a apagar telefone
 * bom com um "Salvar" distraído.
 */
function FormularioDeContato({ devedor }: { devedor: FichaDoPortal['devedor'] }) {
  const clienteConsulta = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});
  const [salvo, setSalvo] = useState(false);

  const [form, setForm] = useState({
    telefone: '',
    telefoneExtra: '',
    email: '',
    logradouro: '',
    cidade: '',
    uf: '',
    cep: '',
  });

  useEffect(() => {
    setForm({
      /* Formatado, e não como está gravado. O banco guarda "5554999891465"; um
         campo mostrando isso convida a recepção a apagar e redigitar — que é
         justamente onde o número se perde. */
      telefone: telefone(devedor.telefone) === '—' ? '' : telefone(devedor.telefone),
      telefoneExtra:
        telefone(devedor.telefoneExtra) === '—' ? '' : telefone(devedor.telefoneExtra),
      email: devedor.email ?? '',
      logradouro: devedor.logradouro ?? '',
      cidade: devedor.cidade ?? '',
      uf: devedor.uf ?? '',
      cep: devedor.cep ?? '',
    });
  }, [devedor]);

  function definir(campo: keyof typeof form, valor: string) {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setSalvo(false);
    setInvalidos((atual) => {
      if (!atual[campo]) return atual;
      const { [campo]: _corrigido, ...resto } = atual;
      return resto;
    });
  }

  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  const salvar = useMutation({
    mutationFn: () => api.substituir(`/portal/devedores/${devedor.id}/contato`, form),
    onSuccess: () => {
      setErro(null);
      setInvalidos({});
      setSalvo(true);
      void clienteConsulta.invalidateQueries({ queryKey: ['portal'] });
    },
    onError: (e) => {
      /* O campo recusado fica MARCADO, além do aviso no topo. Um telefone com um
         dígito a mais some no meio de sete campos se só houver uma frase acima. */
      setSalvo(false);
      setInvalidos(e instanceof FalhaDaApi ? (e.camposInvalidos ?? {}) : {});
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível salvar o contato.');
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Cartao className="h-fit lg:sticky lg:top-6">
      <TituloDeSecao>Contato</TituloDeSecao>

      <form onSubmit={submeter} className="space-y-3">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}
        {salvo && !erro && <Alerta tom="recebido">Contato atualizado.</Alerta>}

        <Campo
          rotulo="Telefone"
          valor={form.telefone}
          aoMudar={(v) => definir('telefone', v)}
          placeholder="(14) 98142-7809"
          auxilio="Com DDD. Celular tem 9 dígitos e começa com 9."
          {...erroDe('telefone')}
        />
        <Campo
          rotulo="Telefone alternativo"
          valor={form.telefoneExtra}
          aoMudar={(v) => definir('telefoneExtra', v)}
          {...erroDe('telefoneExtra')}
        />
        <Campo
          rotulo="E-mail"
          tipo="email"
          valor={form.email}
          aoMudar={(v) => definir('email', v)}
        />
        <Campo
          rotulo="Logradouro"
          valor={form.logradouro}
          aoMudar={(v) => definir('logradouro', v)}
          placeholder="Rua, número, complemento"
        />
        <div className="grid grid-cols-[1fr_4.5rem] gap-3">
          <Campo rotulo="Cidade" valor={form.cidade} aoMudar={(v) => definir('cidade', v)} />
          <Campo
            rotulo="UF"
            valor={form.uf}
            aoMudar={(v) => definir('uf', v.toUpperCase())}
          />
        </div>
        <Campo rotulo="CEP" valor={form.cep} aoMudar={(v) => definir('cep', v)} />

        <Botao tipo="primario" submeter desabilitado={salvar.isPending} className="w-full">
          {salvar.isPending ? 'Salvando…' : 'Salvar contato'}
        </Botao>

        <p className="text-xs text-[color:var(--texto-tenue)]">
          Corrigir o telefone aqui é o que mais destrava caso parado — a cobrança passa
          a alcançar o cliente já no próximo disparo.
        </p>
      </form>
    </Cartao>
  );
}

/** Rótulo e cor de cada marco. O tipo cru (`FASE`, `QUITACAO`) não é para ser lido. */
const MARCO: Readonly<Record<string, { rotulo: string; cor: string }>> = {
  FASE: { rotulo: 'Andamento', cor: 'var(--acento)' },
  ACORDO: { rotulo: 'Acordo', cor: 'var(--color-recebido)' },
  JUDICIAL: { rotulo: 'Judicial', cor: 'var(--color-atencao)' },
  QUITACAO: { rotulo: 'Quitação', cor: 'var(--color-recebido)' },
  DOCUMENTO: { rotulo: 'Documento', cor: 'var(--texto-tenue)' },
  CADASTRO: { rotulo: 'Cadastro', cor: 'var(--texto-tenue)' },
};

function LinhaDoTempo({ marcos }: { marcos: readonly Marco[] }) {
  if (marcos.length === 0) {
    return (
      <p className="py-6 text-sm text-[color:var(--texto-tenue)]">
        Ainda não há andamento registrado. O escritório vai lançar aqui cada passo da
        cobrança — contato, acordo, pagamento e ajuizamento.
      </p>
    );
  }

  return (
    <ol className="space-y-4">
      {marcos.map((m, indice) => {
        const marca = MARCO[m.tipo] ?? { rotulo: m.tipo, cor: 'var(--texto-tenue)' };
        return (
          <li key={`${m.criadoEm}-${indice}`} className="flex gap-3">
            <span
              aria-hidden
              className="mt-1.5 size-2 shrink-0 rounded-full"
              style={{ background: marca.cor }}
            />
            <div className="min-w-0">
              <p className="text-sm text-[color:var(--texto)]">{m.descricao}</p>
              <p className="mt-0.5 text-xs text-[color:var(--texto-tenue)]">
                {marca.rotulo} · {data(m.data)}
                <span className="ml-2" title={dataHora(m.criadoEm)}>
                  registrado em {dataHora(m.criadoEm)}
                </span>
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
